// Session PJ2: the office's panel for ONE project survey (opened under the list on the project's Details tab, not a page of its own).
// Every section the survey's type asks, with what was recorded and who recorded it; the photos by slot; the figures the server works out
// from the measurements; the gaps. The office can correct any value (it is then marked as corrected by the office), read a data plate,
// mark the survey reviewed or reopen it, start a resurvey, or delete it.
//
// Nothing here is a price, and no customer contact is ever shown.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type ProjectSurveyDetailView, type ProjectSurveyPlateProposal } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { Button } from "../../../components/Button";
import { Loader } from "../../../components/Loader";
import { OverflowMenu, overflowDangerItemCls, overflowItemCls } from "../../../components/OverflowMenu";
import { StatusPill } from "../../../components/StatusPill";
import { formatDate } from "../../../lib/format";
import { readRecord, cellPath, fieldPath, notesPath, rowPath, sectionPath, valueAt, SURVEY_NOTES_PATH, type SurveyRecordData, type SurveyValue } from "../../../lib/surveyRecord";
import { SURVEY_TYPES, SURVEY_TYPE_CODES, findField, type SurveyField, type SurveySection, type SurveyType } from "../../../lib/surveyTemplate";
import {
  isSurveyType,
  photoSlotKeys,
  readOtherWords,
  sectionNotes,
  sectionState,
  surveyGaps,
  tableRows,
  visibleFields,
  visibleSections,
  type SectionState,
} from "../../../lib/projectSurveyDraft";
import { EditableValue } from "./EditableValue";
import { SurveyPhotoItem } from "./SurveyPhotoItem";
import {
  noAccessWords,
  proposalValue,
  slotBase,
  specOfField,
  surveyTypeWords,
  valueWords,
} from "./surveyOfficeUi";

const quietBtn = "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";

const SECTION_STATE_WORDS: Record<SectionState, string> = {
  done: "Complete",
  started: "Part done",
  not_started: "Nothing recorded",
  no_access: "Could not access",
};

export function SurveyPanel({
  surveyId,
  onClose,
  onChanged,
  onRequestDelete,
  onResurveyed,
}: {
  surveyId: string;
  onClose: () => void;
  /** Called after anything changed, so the list above re-reads the statuses. */
  onChanged: () => void;
  onRequestDelete: (survey: { id: string; label: string | null; kind: "site" | "lift" }) => void;
  /** A resurvey made a new survey: open it. */
  onResurveyed: (newSurveyId: string) => void;
}) {
  const [detail, setDetail] = useState<ProjectSurveyDetailView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // A photo was removed but its Drive file could not be deleted: said, so the office knows it is still in the Survey folder.
  const [photoNote, setPhotoNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const top = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      setDetail(await api.projectSurveysOffice.detail(surveyId));
      setLoadError(null);
    } catch (e) {
      setLoadError(friendlyApiError(e, "Could not load this survey."));
    }
  }, [surveyId]);

  useEffect(() => {
    setDetail(null);
    void refresh();
  }, [refresh]);

  useEffect(() => {
    top.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [surveyId]);

  const record: SurveyRecordData = useMemo(() => (detail ? readRecord(detail.record) : { cells: {} }), [detail]);
  const type: SurveyType | null = detail && isSurveyType(detail.surveyType) ? detail.surveyType : null;

  // The office's cells, saved through the one record route. The server times and stamps them as the office's.
  const saveCells = useCallback(
    async (cells: Record<string, { v: SurveyValue | null; src?: "typed" | "photo_confirmed" }>): Promise<string | null> => {
      try {
        await api.projectSurveysOffice.saveRecord(surveyId, { cells });
        await refresh();
        onChanged();
        return null;
      } catch (e) {
        return friendlyApiError(e, "Could not save that change. Please try again.");
      }
    },
    [surveyId, refresh, onChanged],
  );

  async function run(fn: () => Promise<void>, fallback: string) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(friendlyApiError(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  const setType = (value: string) =>
    run(async () => {
      await api.projectSurveysOffice.patch(surveyId, { surveyType: value === "" ? null : value });
      await refresh();
      onChanged();
    }, "Could not change the survey type.");

  // Session PJ2F3: a review carries the version this panel shows, so a survey that moved since it loaded (a visit's figures merged, say) is
  // refused rather than reviewed unseen. On that refusal the panel reloads, so the office sees the new figures before pressing again.
  const review = (reopen: boolean) =>
    run(async () => {
      try {
        await api.projectSurveysOffice.review(surveyId, reopen, reopen ? null : (detail?.version ?? null));
      } catch (e) {
        if (e instanceof ApiError && e.body?.error === "survey_changed") {
          await refresh();
          onChanged();
        }
        throw e;
      }
      await refresh();
      onChanged();
    }, reopen ? "Could not reopen this survey." : "Could not mark this survey reviewed.");

  const resurvey = () =>
    run(async () => {
      const out = await api.projectSurveysOffice.resurvey(surveyId);
      onChanged();
      onResurveyed(out.survey.id);
    }, "Could not start a resurvey.");

  if (loadError) {
    return (
      <div ref={top} className="mt-4 rounded-ele border border-ele-purple-accent p-4" data-survey-panel>
        <p role="alert" className="text-sm text-ele-danger">{loadError}</p>
        <button type="button" className={`${quietBtn} mt-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  if (!detail) {
    return (
      <div ref={top} className="mt-4 rounded-ele border border-ele-purple-accent p-4" data-survey-panel>
        <Loader shape="inline" label="Loading the survey..." />
      </div>
    );
  }

  const label = detail.kind === "site" ? "Site survey" : (detail.label ?? "Lift");
  const live = detail.status !== "superseded";
  const sections = visibleSections(detail.formVersion, detail.kind, type);
  const slotKeysShown = new Set<string>();
  for (const s of sections) for (const f of visibleFields(detail.formVersion, s, type, record)) if (f.kind === "photo") for (const k of photoSlotKeys(f, record)) slotKeysShown.add(k.slotKey);
  const photoSlotsTaken = new Set(detail.photos.map((p) => p.slotKey));
  const gaps = surveyGaps(detail.formVersion, detail.kind, type, record, photoSlotsTaken);
  const otherPhotos = detail.photos.filter((p) => !slotKeysShown.has(p.slotKey));

  // A plate proposal is accepted into the field it names, if this survey has that field.
  const fieldFor = (fieldKey: string): SurveyField | null => findField(detail.formVersion, fieldKey)?.field ?? null;
  const describeCurrent = (fieldKey: string): string => {
    const f = fieldFor(fieldKey);
    return f ? valueWords(specOfField(f), valueAt(record, fieldPath(fieldKey))) : "";
  };
  const canAccept = (p: ProjectSurveyPlateProposal): boolean => proposalValue(fieldFor(p.fieldKey), p) !== null;
  const accept = async (p: ProjectSurveyPlateProposal): Promise<string | null> => {
    const v = proposalValue(fieldFor(p.fieldKey), p);
    if (v === null) return "That value does not fit this field.";
    return saveCells({ [fieldPath(p.fieldKey)]: { v, src: "photo_confirmed" } });
  };

  const drafted = detail.supplierSheetDraftedAt;

  return (
    <div ref={top} className="mt-4 rounded-ele border border-ele-purple-accent p-4" data-survey-panel aria-label={`Survey: ${label}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-heading text-lg text-ele-strong">{label}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <StatusPill domain="projectSurvey" status={detail.status} />
            {detail.kind === "lift" ? <span className="text-sm text-ele-body">{surveyTypeWords(detail.surveyType)}</span> : null}
            {detail.addedOnSite ? <span className="text-xs text-ele-muted">Added by the engineer on site</span> : null}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {detail.status === "submitted" ? (
            <Button size="sm" disabled={busy} onClick={() => void review(false)}>
              Mark reviewed
            </Button>
          ) : null}
          {detail.status === "reviewed" ? (
            <ConfirmButton onConfirm={() => void review(true)} label="Reopen" confirmLabel="Reopen it?" busy={busy} busyLabel="Reopening..." className={quietBtn} />
          ) : null}
          {live ? (
            <OverflowMenu label={`More actions for ${label}`} closeOnSelect>
              {detail.status === "submitted" || detail.status === "reviewed" ? (
                <ConfirmButton onConfirm={() => void resurvey()} label="Start a resurvey" confirmLabel="Start it?" busy={busy} busyLabel="Starting..." className={overflowItemCls} armedClassName="!text-ele-strong font-semibold" />
              ) : null}
              <button role="menuitem" className={overflowDangerItemCls} onClick={() => onRequestDelete({ id: detail.id, label: detail.label, kind: detail.kind })}>
                Delete this survey
              </button>
            </OverflowMenu>
          ) : null}
          <button type="button" className={quietBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {actionError ? <p role="alert" className="mt-2 text-sm text-ele-danger">{actionError}</p> : null}
      {photoNote ? <p role="status" className="mt-2 text-sm text-ele-body" data-photo-note>{photoNote}</p> : null}
      {detail.status === "reviewed" && drafted ? (
        <p className="mt-2 text-xs text-ele-body">A supplier data sheet was drafted from this survey on {formatDate(drafted)}. Reopening it does not recall that draft.</p>
      ) : null}
      {detail.status === "superseded" ? <p className="mt-2 text-xs text-ele-body">A newer survey has replaced this one. It is kept as a record and cannot be reviewed.</p> : null}

      {detail.kind === "lift" && live ? (
        <div className="mt-3">
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="survey-type">Survey type</label>
          <select
            id="survey-type"
            className="w-full max-w-sm rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green max-sm:min-h-11"
            value={detail.surveyType ?? ""}
            disabled={busy}
            onChange={(e) => void setType(e.target.value)}
          >
            <option value="">Not chosen</option>
            {SURVEY_TYPE_CODES.map((c) => (
              <option key={c} value={c}>{SURVEY_TYPES[c]}</option>
            ))}
          </select>
        </div>
      ) : null}

      {detail.visits.length > 0 ? (
        <div className="mt-3">
          <p className="font-menu text-xs uppercase tracking-wide text-ele-body">Visits that worked on it</p>
          <ul className="mt-1 space-y-0.5 text-sm text-ele-strong">
            {detail.visits.map((v) => (
              <li key={v.jobId}>
                {v.engineerName ?? "No engineer yet"}, {v.scheduledFor ? formatDate(v.scheduledFor) : "no date"}
                {v.outcome === "not_surveyed" ? " (not surveyed)" : ""}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {detail.kind === "lift" && type === null ? (
        <p className="mt-3 text-sm text-ele-body">Choose the survey type to see the questions for it.</p>
      ) : null}

      {detail.figures && detail.figures.length > 0 ? (
        <div className="mt-4" data-figures>
          <h4 className="font-heading text-base text-ele-strong">Worked out from the measurements</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {detail.figures.map((f) => (
              <li key={f.key} className="text-ele-body">
                <span className="text-ele-muted">{f.label}: </span>
                {f.result.ok ? (
                  <span className="text-ele-strong">
                    {f.result.value}
                    {f.result.unit === "mm" ? " mm" : ""} <span className="text-xs text-ele-muted">({f.result.workings})</span>
                  </span>
                ) : (
                  <span>Not enough measured yet: {f.result.missing.join("; ")}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {gaps.length > 0 ? (
        <details className="mt-4 rounded-ele border border-ele-card-border p-2" data-gaps>
          <summary className="cursor-pointer text-sm text-ele-strong max-sm:min-h-11">Gaps ({detail.gapsCount})</summary>
          <ul className="mt-2 space-y-1 text-sm text-ele-body">
            {gaps.map((g) => (
              <li key={g.section}>
                <span className="text-ele-strong">{g.section}: </span>
                {g.noAccess
                  ? "the whole section could not be accessed"
                  : [g.blank.length ? `not filled in: ${g.blank.join(", ")}` : "", g.notAccessible.length ? `could not access: ${g.notAccessible.join(", ")}` : "", g.photosMissing ? `${g.photosMissing} photo${g.photosMissing === 1 ? "" : "s"} missing` : ""]
                      .filter(Boolean)
                      .join("; ")}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      <div className="mt-4">
        <EditableValue
          label="Notes on the whole survey"
          spec={{ kind: "text" }}
          cell={record.cells[SURVEY_NOTES_PATH]}
          value={valueAt(record, SURVEY_NOTES_PATH)}
          onSave={(v) => saveCells({ [SURVEY_NOTES_PATH]: { v } })}
        />
      </div>

      <div className="mt-3 space-y-2">
        {sections.map((section) => (
          <SectionBlock
            key={section.key}
            section={section}
            detail={detail}
            record={record}
            type={type}
            saveCells={saveCells}
            describeCurrent={describeCurrent}
            canAccept={canAccept}
            accept={accept}
            onPhotoDeleted={(note) => {
              setPhotoNote(note ?? null);
              void refresh();
              onChanged();
            }}
          />
        ))}
        {otherPhotos.length > 0 ? (
          <details className="rounded-ele border border-ele-card-border p-3">
            <summary className="cursor-pointer font-heading text-base text-ele-strong max-sm:min-h-11">Other photos ({otherPhotos.length})</summary>
            <ul className="mt-2 flex flex-wrap gap-3">
              {otherPhotos.map((p) => (
                <SurveyPhotoItem
                  key={p.id}
                  surveyId={detail.id}
                  photo={p}
                  slotLabel={slotBase(p.slotKey) === "extra" ? "Extra photo" : slotBase(p.slotKey)}
                  describeCurrent={describeCurrent}
                  canAccept={canAccept}
                  onAccept={accept}
                  onDeleted={(note) => {
                    setPhotoNote(note ?? null);
                    void refresh();
                    onChanged();
                  }}
                />
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </div>
  );
}

// ---- One section ------------------------------------------------------------------------------------------------------

function SectionBlock({
  section,
  detail,
  record,
  type,
  saveCells,
  describeCurrent,
  canAccept,
  accept,
  onPhotoDeleted,
}: {
  section: SurveySection;
  detail: ProjectSurveyDetailView;
  record: SurveyRecordData;
  type: SurveyType | null;
  saveCells: (cells: Record<string, { v: SurveyValue | null; src?: "typed" | "photo_confirmed" }>) => Promise<string | null>;
  describeCurrent: (fieldKey: string) => string;
  canAccept: (p: ProjectSurveyPlateProposal) => boolean;
  accept: (p: ProjectSurveyPlateProposal) => Promise<string | null>;
  onPhotoDeleted: (driveNote?: string) => void;
}) {
  const state = sectionState(detail.formVersion, section, type, record);
  const fields = visibleFields(detail.formVersion, section, type, record);
  const sectionNa = record.cells[sectionPath(section.key)]?.na;
  const notes = sectionNotes(record, section.key);
  return (
    <details className="rounded-ele border border-ele-card-border p-3" data-section={section.key}>
      <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 max-sm:min-h-11">
        <span className="font-heading text-base text-ele-strong">{section.title}</span>
        <span className="text-xs text-ele-muted">{SECTION_STATE_WORDS[state]}</span>
      </summary>
      <div className="mt-2">
        {sectionNa ? <p className="mb-2 text-sm text-ele-strong">The whole section: {noAccessWords(sectionNa)}</p> : null}
        {fields.map((field) => {
          if (field.kind === "photo") {
            return (
              <div key={field.key} className="border-b border-ele-card-border py-2 last:border-b-0">
                {photoSlotKeys(field, record).map((slot) => {
                  const photos = detail.photos.filter((p) => p.slotKey === slot.slotKey);
                  return (
                    <div key={slot.slotKey} className="mb-2 last:mb-0" data-slot={slot.slotKey}>
                      <p className="text-xs text-ele-muted">{slot.label}</p>
                      {photos.length === 0 ? (
                        <p className="text-sm text-ele-muted">No photo</p>
                      ) : (
                        <ul className="mt-1 flex flex-wrap gap-3">
                          {photos.map((p) => (
                            <SurveyPhotoItem
                              key={p.id}
                              surveyId={detail.id}
                              photo={p}
                              slotLabel={slot.label}
                              describeCurrent={describeCurrent}
                              canAccept={canAccept}
                              onAccept={accept}
                              onDeleted={onPhotoDeleted}
                            />
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }
          if (field.kind === "table") {
            const rows = tableRows(field, record);
            return (
              <div key={field.key} className="border-b border-ele-card-border py-2 last:border-b-0">
                <p className="font-heading text-sm text-ele-strong">{field.label}</p>
                {rows.length === 0 ? (
                  <p className="text-sm text-ele-muted">No floors recorded yet, so there are no rows.</p>
                ) : (
                  <ol className="mt-1 space-y-2">
                    {rows.map((r) => {
                      const rowNa = record.cells[rowPath(field.key, r.key)]?.na;
                      return (
                        <li key={r.key} className="rounded-ele border border-ele-card-border p-2" aria-label={r.label}>
                          <p className="font-heading text-sm text-ele-strong">{r.label}</p>
                          {rowNa ? <p className="text-sm text-ele-strong">{noAccessWords(rowNa)}</p> : null}
                          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                            {(field.columns ?? []).map((c) => {
                              const p = cellPath(field.key, r.key, c.key);
                              return (
                                <EditableValue
                                  key={c.key}
                                  label={`${c.label}, ${r.label}`}
                                  spec={{ kind: c.kind, options: c.options, unit: c.unit }}
                                  cell={record.cells[p]}
                                  value={valueAt(record, p)}
                                  onSave={(v) => saveCells({ [p]: { v } })}
                                />
                              );
                            })}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            );
          }
          const path = fieldPath(field.key);
          return (
            <EditableValue
              key={field.key}
              label={field.unit ? `${field.label} (${field.unit})` : field.label}
              spec={specOfField(field)}
              cell={record.cells[path]}
              value={valueAt(record, path)}
              otherWords={readOtherWords(notes, field)}
              onSave={(v) => saveCells({ [path]: { v } })}
            />
          );
        })}
        <EditableValue
          label={`Notes: ${section.title}`}
          spec={{ kind: "text" }}
          cell={record.cells[notesPath(section.key)]}
          value={valueAt(record, notesPath(section.key))}
          onSave={(v) => saveCells({ [notesPath(section.key)]: { v } })}
        />
      </div>
    </details>
  );
}
